/**
 * Общие элементы старых экранов — на той же системе, что финансовый блок:
 * карточка — `.panel`, плитка — `.kpi`, оговорка — `.caveat`.
 * Отдельной «второй» дизайн-системы у сводки, отчёта, загрузок и админки нет.
 */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Icon, type IconName } from './Icon'
import { prefersReducedMotion } from '../finance/motion'

export function Card({
  title,
  subtitle,
  actions,
  children,
  flush,
  refreshing,
  className,
  index,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  /** Содержимое без внутренних отступов: таблица во всю ширину. */
  flush?: boolean
  /** Пришли новые параметры, на экране пока прежние данные. */
  refreshing?: boolean
  className?: string
  /** Место в каскаде появления (0–7): карточки экрана появляются по очереди. */
  index?: number
}) {
  return (
    <section
      className={`panel${index !== undefined ? ' cascade' : ''}${refreshing ? ' panel--refreshing' : ''}${className ? ` ${className}` : ''}`}
      style={index !== undefined ? ({ '--i': Math.min(index, 7) } as CSSProperties) : undefined}
      aria-busy={refreshing || undefined}
    >
      {(title || actions) && (
        <header className="panel__header">
          <div className="panel__heading">
            {typeof title === 'string' ? <h3 className="panel__title">{title}</h3> : title}
            {subtitle && <div className="panel__subtitle">{subtitle}</div>}
          </div>
          {actions && <div className="row row--tight panel__actions">{actions}</div>}
        </header>
      )}
      <div className={flush ? 'panel__body panel__body--flush' : 'panel__body'}>
        <div className="panel__content">{children}</div>
      </div>
      <span className="panel__progress" aria-hidden="true" />
    </section>
  )
}

export function Stat({
  label,
  value,
  hint,
  tone,
  delta,
  deltaLabel,
  /** Для показателей, где рост — это плохо: например выручка без себестоимости. */
  invertDelta,
  /** Изменение в процентных пунктах, а не в процентах. */
  points,
}: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  tone?: 'good' | 'bad'
  delta?: number | null
  deltaLabel?: string
  invertDelta?: boolean
  points?: boolean
}) {
  const good = delta === null || delta === undefined ? null : invertDelta ? delta < 0 : delta > 0

  return (
    <div className="kpi kpi--static">
      <div className="kpi__top">
        <span className="kpi__label">{label}</span>
      </div>
      <div className={`kpi__value mono${tone === 'bad' ? ' kpi__value--bad' : ''}`}>{value}</div>
      <div className="kpi__foot">
        <div className="kpi__meta">
          <div className="kpi__line">
          {delta !== undefined && delta !== null && (
            <span className="kpi__delta">
              {/* Направление несёт стрелка, цвет лишь усиливает. */}
              <span className={`delta ${good ? 'delta--up' : delta === 0 ? 'delta--flat' : 'delta--down'}`}>
                <Icon name={delta > 0 ? 'tri-up' : delta < 0 ? 'tri-down' : 'dot'} size={10} />
                {delta > 0 ? '+' : delta < 0 ? '−' : ''}
                {Math.abs(delta).toLocaleString('ru-RU', { maximumFractionDigits: points ? 2 : 1 })}
                {points ? ' п.п.' : '%'}
              </span>
              {deltaLabel && <span className="muted">{deltaLabel}</span>}
            </span>
          )}
          </div>
          {hint && <div className="kpi__hint">{hint}</div>}
        </div>
      </div>
    </div>
  )
}

const CAVEAT_ICON: Record<'info' | 'warn' | 'bad', IconName> = { info: 'info', warn: 'warning', bad: 'alert' }

/**
 * Оговорка: одна компонента на всю систему (info / warn / bad).
 * Предупреждения из API показываются буквально: неполнота себестоимости
 * и предварительные периоды должны быть видны руководителю.
 */
export function Notice({
  tone = 'info',
  icon,
  children,
}: {
  tone?: 'info' | 'warn' | 'bad' | 'plain' | 'good'
  icon?: IconName
  children: ReactNode
}) {
  const kind = tone === 'plain' || tone === 'good' ? 'info' : tone
  return (
    <div className={`caveat caveat--${tone === 'good' ? 'good' : kind}`} role={tone === 'bad' ? 'alert' : undefined}>
      <Icon name={icon ?? CAVEAT_ICON[kind]} className="caveat__icon" />
      <div className="caveat__text">{children}</div>
    </div>
  )
}

export function Badge({ tone, children }: { tone?: 'good' | 'warn' | 'bad' | 'accent'; children: ReactNode }) {
  return <span className={tone ? `badge badge--${tone}` : 'badge'}>{children}</span>
}

export function Empty({ icon, title, hint, action }: { icon?: IconName; title: string; hint?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon && <Icon name={icon} size={22} className="empty__icon" />}
      <div className="empty__title">{title}</div>
      {hint && <div className="empty__hint">{hint}</div>}
      {action && <div className="empty__action">{action}</div>}
    </div>
  )
}

/** Скелетон строк таблицы: высота строки та же, что у данных. */
export function Loading({ rows = 3, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="skeleton-table" aria-busy="true" aria-label="Загрузка">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="skeleton-table__row">
          {Array.from({ length: columns }, (_, column) => (
            <div key={column} className="skeleton" style={{ height: 12, width: column === 0 ? '70%' : `${45 + ((row * 17 + column * 23) % 40)}%` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** Скелетон карточки целиком: шапка, плитки, тело заданной высоты. */
export function SkeletonPanel({ height = 220, title = true }: { height?: number; title?: boolean }) {
  return (
    <section className="panel" aria-busy="true" aria-label="Загрузка">
      {title && (
        <header className="panel__header">
          <div className="panel__heading">
            <div className="skeleton" style={{ height: 14, width: 180 }} />
            <div className="skeleton" style={{ height: 10, width: 260, marginTop: 8 }} />
          </div>
        </header>
      )}
      <div className="panel__body">
        <div className="skeleton" style={{ height }} />
      </div>
    </section>
  )
}

/**
 * Скелетон карточки — та же разметка строк, что у готовой: подпись, значение
 * с мини-трендом, изменение, подсказка. Полосы стоят в строках той же высоты,
 * поэтому, когда приходят цифры, карточка не меняет высоту.
 */
export function KpiSkeleton({ index = 0, static: plain = false }: { index?: number; static?: boolean }) {
  return (
    <div className={`kpi kpi--skeleton cascade${plain ? ' kpi--static' : ''}`} style={{ '--i': index } as CSSProperties} aria-busy="true" aria-label="Загрузка">
      <div className="kpi__top">
        <span className="kpi__label"><span className="skeleton skeleton--text" style={{ width: '55%' }} /></span>
      </div>
      <div className="kpi__main">
        <div className="kpi__value"><span className="skeleton skeleton--text" style={{ width: '62%' }} /></div>
      </div>
      <div className="kpi__foot">
        <div className="kpi__meta">
          <div className="kpi__line"><span className="skeleton skeleton--text" style={{ width: '48%' }} /></div>
          <div className="kpi__hint"><span className="skeleton skeleton--text" style={{ width: '36%' }} /></div>
        </div>
      </div>
    </div>
  )
}

export function SkeletonKpis({ count = 4 }: { count?: number }) {
  return (
    <div className={`kpi-grid kpi-grid--n${Math.min(count, 4)}`} aria-busy="true" aria-label="Загрузка">
      {Array.from({ length: count }, (_, index) => (
        <KpiSkeleton key={index} index={index} />
      ))}
    </div>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="tiny muted">{hint}</span>}
    </label>
  )
}

/**
 * Обёртка широкой таблицы: прокрутка внутри, тень у края, за которым
 * есть ещё колонки, и закреплённая первая колонка (класс `sticky-first`).
 */
export function TableWrap({
  children,
  stickyFirst = true,
  cards = false,
  className,
}: {
  children: ReactNode
  stickyFirst?: boolean
  /** На телефоне строка становится карточкой «ключ — значение»: журналы и загрузки. */
  cards?: boolean
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  useEffect(() => {
    const node = ref.current
    if (!node) return
    const update = () => {
      const max = node.scrollWidth - node.clientWidth
      setEdges({ left: node.scrollLeft > 1, right: node.scrollLeft < max - 1 })
    }
    update()
    node.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(node)
    if (node.firstElementChild) observer.observe(node.firstElementChild)
    return () => {
      node.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [])

  // Подписи для карточек берутся из заголовков таблицы: размечать каждую
  // ячейку руками не нужно, и подпись не разойдётся с заголовком.
  useEffect(() => {
    const node = ref.current
    if (!cards || !node) return
    const label = () => {
      for (const table of node.querySelectorAll('table')) {
        const heads = [...table.querySelectorAll('thead tr:first-child th')].map((th) => th.textContent?.trim() ?? '')
        for (const row of table.querySelectorAll('tbody tr')) {
          ;[...row.children].forEach((cell, index) => {
            const text = heads[index] ?? ''
            if ((cell as HTMLElement).dataset.label !== text) (cell as HTMLElement).dataset.label = text
          })
        }
      }
    }
    label()
    const observer = new MutationObserver(label)
    observer.observe(node, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [cards])

  return (
    <div className={`table-scroll${edges.left ? ' table-scroll--left' : ''}${edges.right ? ' table-scroll--right' : ''}${className ? ` ${className}` : ''}`}>
      <div ref={ref} className={`table-wrap${stickyFirst ? ' sticky-first' : ''}${cards ? ' table-wrap--cards' : ''}`}>
        {children}
      </div>
    </div>
  )
}

/** Переключатель да/нет. */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  hint,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: ReactNode
  disabled?: boolean
  hint?: ReactNode
}) {
  return (
    <label className={`toggle${disabled ? ' toggle--disabled' : ''}`}>
      <input type="checkbox" role="switch" className="toggle__input" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span className="toggle__track" aria-hidden="true">
        <span className="toggle__thumb" />
      </span>
      <span className="toggle__text">
        <span className="toggle__label">{label}</span>
        {hint && <span className="toggle__hint">{hint}</span>}
      </span>
    </label>
  )
}

/** Чип множественного выбора: галочка и мягкий акцент, без сплошной заливки. */
export function CheckChip({
  checked,
  onClick,
  children,
  disabled,
  title,
}: {
  checked: boolean
  onClick: () => void
  children: ReactNode
  disabled?: boolean
  title?: string
}) {
  return (
    <button type="button" className={`chip${checked ? ' chip--on' : ''}`} aria-pressed={checked} onClick={onClick} disabled={disabled} title={title}>
      <Icon name="check" size={12} className="chip__check" />
      {children}
    </button>
  )
}

/**
 * Вкладки с одной полосой-подчёркиванием: при смене вкладки полоса едет
 * к новой (translateX + scaleX от ширины 100 px, --dur-section), а не
 * исчезает под одной и появляется под другой. Содержимое вкладки
 * оборачивается в `.tab-panel` с ключом — оно проявляется (fin-enter).
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: Array<{ value: T; label: ReactNode }>
  value: T
  onChange: (value: T) => void
  label: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [bar, setBar] = useState<{ x: number; width: number } | null>(null)
  const [ready, setReady] = useState(false)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const measure = () => {
      const active = node.querySelector<HTMLElement>('[aria-selected="true"]')
      if (!active) return setBar(null)
      setBar({ x: active.offsetLeft + 8, width: Math.max(0, active.offsetWidth - 16) })
    }
    measure()
    // Выбранная вкладка не должна прятаться за краем узкого экрана.
    const active = node.querySelector<HTMLElement>('[aria-selected="true"]')
    if (active && (active.offsetLeft < node.scrollLeft || active.offsetLeft + active.offsetWidth > node.scrollLeft + node.clientWidth)) {
      node.scrollTo({ left: active.offsetLeft - 24, behavior: prefersReducedMotion() || !bar ? 'auto' : 'smooth' })
    }
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [value, items.length])

  // Первая расстановка — без движения: полоса не выезжает из левого края при открытии экрана.
  useEffect(() => {
    if (!bar || ready) return
    const id = requestAnimationFrame(() => setReady(true))
    return () => cancelAnimationFrame(id)
  }, [bar, ready])

  return (
    <div className="tabs" role="tablist" aria-label={label} ref={ref}>
      {items.map((item) => (
        <button
          key={item.value}
          role="tab"
          type="button"
          aria-selected={item.value === value}
          className={`tab${item.value === value ? ' tab--on' : ''}`}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
      {bar && (
        <span
          className={`tabs__bar${ready ? ' tabs__bar--ready' : ''}`}
          style={{ transform: `translateX(${bar.x}px) scaleX(${bar.width / 100})` }}
          aria-hidden="true"
        />
      )}
    </div>
  )
}
